// Landing Page JavaScript
// Handles authentication modals and landing page interactions

import { auth } from './auth.js';
import { toast } from './components/ui.js';
import { config } from './config.js';
import { telemetry } from './telemetry.js';

/**
 * Landing Page Controller
 */
class LandingPage {
    constructor() {
        this.capabilities = {
            publicDirectoryEnabled: false,
            digitalPlayEnabled: false,
            digitalPlayAccountsEnabled: false,
            publicPricingEnabled: config.app.features.publicPricing,
            trialDays: 7,
            trialMessage: 'Try your first quiz on us.',
        };
        this.mobileMenuOpen = false;
        this.init().catch(() => {
            toast.error('Could not initialize landing page');
        });
    }

    async init() {
        // Check if already logged in
        if (auth.isLoggedIn()) {
            window.location.href = this.getLoggedInRedirect();
            return;
        }

        await this.loadCapabilities();
        this.setupModals();
        this.setupAuthForms();
        this.setupNavigation();
        await this.loadPublicStats();
        await this.loadCmsContent();
        await this.loadPublicPricing();
    }

    async loadCapabilities() {
        try {
            const response = await fetch(`${config.apiUrl}${config.endpoints.publicCapabilities}`);
            if (!response.ok) {
                this.applyCapabilities();
                return;
            }

            const payload = await response.json().catch(() => null);
            const data = payload?.data;
            if (!data) {
                this.applyCapabilities();
                return;
            }

            this.capabilities = {
                publicDirectoryEnabled: Boolean(data.publicDirectoryEnabled),
                digitalPlayEnabled: Boolean(data.digitalPlayEnabled),
                digitalPlayAccountsEnabled: Boolean(data.digitalPlayAccountsEnabled),
                publicPricingEnabled: Boolean(data.publicPricingEnabled),
                trialDays: Number(data.trialDays || 7),
                trialMessage: String(data.trialMessage || 'Try your first quiz on us.'),
            };
        } catch {
            // Safe mode: keep optional public feature links hidden on failure
        } finally {
            this.applyCapabilities();
        }
    }

    applyCapabilities() {
        document.querySelectorAll('[data-capability-link]').forEach((link) => {
            const capability = link.getAttribute('data-capability-link');
            if (capability === 'publicDirectory') {
                link.hidden = !this.capabilities.publicDirectoryEnabled;
            } else if (capability === 'digitalPlay') {
                link.hidden = !this.capabilities.digitalPlayEnabled;
            }
        });
    }

    async loadCmsContent() {
        try {
            const response = await fetch(`${config.apiUrl}${config.endpoints.publicCmsPage('landing')}`);
            if (!response.ok) return;
            const payload = await response.json();
            const content = payload?.data?.content;
            if (!content) return;

            const hero = content.hero || {};
            const pricing = content.pricing || {};
            const cta = content.cta || {};
            const faq = Array.isArray(content.faq) ? content.faq : [];
            const seo = content.seo || {};

            this.setText('cmsHeroBadge', hero.badge);
            this.setText('cmsHeroTitle', hero.title);
            this.setText('cmsHeroDescription', hero.description);
            this.setButtonText('heroGetStartedBtn', hero.primaryCta);
            this.setText('cmsPricingTitle', pricing.title);
            this.setText('cmsPricingSubtitle', pricing.subtitle);
            this.setText('cmsPricingPlanName', pricing.planName);
            this.setText('cmsPricingCurrency', pricing.currency || '£');
            this.setText('cmsPricingAmount', pricing.price ? String(pricing.price).replace(/[^\d.]/g, '') : null);
            this.setText('cmsPricingPeriod', pricing.period);
            this.setText('cmsCtaTitle', cta.title);
            this.setText('cmsCtaDescription', cta.description);
            this.setButtonText('ctaGetStartedBtn', cta.button);

            if (faq.length) {
                this.renderFaq(faq);
            }

            if (seo.title) {
                const title = String(seo.title);
                document.title = title;
                this.setMetaContent('meta[property="og:title"]', title);
                this.setMetaContent('meta[name="twitter:title"]', title);
            }
            if (seo.description) {
                const description = String(seo.description);
                let metaDescription = document.querySelector('meta[name="description"]');
                if (!metaDescription) {
                    metaDescription = document.createElement('meta');
                    metaDescription.setAttribute('name', 'description');
                    document.head.appendChild(metaDescription);
                }
                metaDescription.setAttribute('content', description);
                this.setMetaContent('meta[property="og:description"]', description);
                this.setMetaContent('meta[name="twitter:description"]', description);
            }
        } catch {
            // Keep static defaults when CMS payload is unavailable
        }
    }

    async loadPublicPricing() {
        if (!this.capabilities.publicPricingEnabled) {
            return;
        }

        try {
            const response = await fetch(`${config.apiUrl}${config.endpoints.publicPricing}`);
            if (!response.ok) return;
            const payload = await response.json().catch(() => null);
            const data = payload?.data;
            if (!data) return;

            const basePence = Number(data?.model?.weeklyBasePerPubPence || 500);
            const trialDays = Number(data?.model?.trialDays || this.capabilities.trialDays || 7);
            const trialMessage = String(
                data?.model?.trialMessage ||
                this.capabilities.trialMessage ||
                'Try your first quiz on us.'
            );
            const currency = String(data?.currency || 'gbp').toUpperCase();
            const amount = (basePence / 100).toFixed(2).replace(/\.00$/, '');
            const discountSummary = Array.isArray(data?.model?.multiPubDiscounts)
                ? data.model.multiPubDiscounts
                    .map((tier) => {
                        const percent = Number.isFinite(Number(tier.discountPercent))
                            ? Number(tier.discountPercent)
                            : Math.round(Number(tier.discountRate || 0) * 100);
                        return `${percent}% (${tier.minPubs}+)`;
                    })
                    .join(', ')
                : '';
            const addonAmount = Number(data?.model?.secondQuizAddonWeeklyPence || 200) / 100;
            const pictureRoundAmount = Number(data?.model?.pictureRoundAddonWeeklyPence ?? 200) / 100;

            this.setText('cmsPricingCurrency', currency === 'GBP' ? '£' : currency);
            this.setText('cmsPricingAmount', amount);
            this.setText('cmsPricingPeriod', '/week per pub');
            this.setText(
                'howItWorksPricingCopy',
                `From £${amount}/week per pub (ex VAT), with multi-pub discounts${discountSummary ? ` (${discountSummary})` : ''}, extra weekly quizzes at £${addonAmount.toFixed(2)} each, included weekly generation, and picture rounds at £${pictureRoundAmount.toFixed(2)}/week per pub.`
            );
            this.setText(
                'cmsPricingTrialNote',
                `${trialMessage} ${trialDays}-day free trial access starts when you create your account and includes 1 quiz generation. Prices shown ex VAT. Cancel anytime.`
            );
            this.setText(
                'cmsPricingFaqTrial',
                `Yes. ${trialMessage} ${trialDays}-day free trial access starts when you create your account and includes 1 quiz generation.`
            );
        } catch {
            // Keep static defaults if pricing API is unavailable
        }
    }

    async loadPublicStats() {
        try {
            const response = await fetch(`${config.apiUrl}${config.endpoints.publicStats}`);
            if (!response.ok) {
                return;
            }

            const payload = await response.json().catch(() => null);
            const data = payload?.data;
            if (!data) {
                return;
            }

            const activePubs = Number(data.activePubsDisplayCount);
            const quizzesGenerated = Number(data.quizzesGeneratedDisplayCount);

            if (Number.isFinite(activePubs)) {
                this.setText('heroActivePubsStat', `${activePubs.toLocaleString()}+`);
            }

            if (Number.isFinite(quizzesGenerated)) {
                this.setText('heroGeneratedQuizzesStat', `${quizzesGenerated.toLocaleString()}+`);
            }
        } catch {
            // Keep baseline stats visible if the public stats endpoint is unavailable
        }
    }

    /**
     * Setup modal functionality
     */
    setupModals() {
        // Get modal elements
        this.loginModal = document.getElementById('loginModal');
        this.registerModal = document.getElementById('registerModal');

        const loginBtn = document.getElementById('loginBtn');
        const getStartedBtn = document.getElementById('getStartedBtn');
        const heroGetStartedBtn = document.getElementById('heroGetStartedBtn');
        const pricingGetStartedBtn = document.getElementById('pricingGetStartedBtn');
        const ctaGetStartedBtn = document.getElementById('ctaGetStartedBtn');

        loginBtn?.addEventListener('click', () => {
            this.showLoginModal();
        });

        [getStartedBtn, heroGetStartedBtn, pricingGetStartedBtn, ctaGetStartedBtn].forEach(btn => {
            btn?.addEventListener('click', () => {
                telemetry.track('landing_cta_clicked', { source: btn.id });
                this.showRegisterModal();
            });
        });

        // Close buttons
        document.querySelectorAll('.modal-close, [data-modal-close]').forEach(btn => {
            btn.addEventListener('click', () => this.hideAllModals());
        });

        // Click outside to close
        document.querySelectorAll('.modal-overlay').forEach(overlay => {
            overlay.addEventListener('click', () => this.hideAllModals());
        });

        // Switch between login and register
        document.getElementById('showRegister')?.addEventListener('click', (e) => {
            e.preventDefault();
            this.hideAllModals();
            this.showRegisterModal();
        });

        document.getElementById('showLogin')?.addEventListener('click', (e) => {
            e.preventDefault();
            this.hideAllModals();
            this.showLoginModal();
        });
    }

    /**
     * Show login modal
     */
    showLoginModal() {
        this.closeMobileMenu({ restoreFocus: false });
        this.hideAllModals();
        this.loginModal.classList.add('active');
        document.getElementById('loginEmail')?.focus();
    }

    /**
     * Show register modal
     */
    showRegisterModal() {
        this.closeMobileMenu({ restoreFocus: false });
        this.hideAllModals();
        this.registerModal.classList.add('active');
        document.getElementById('registerVenue')?.focus();
    }

    /**
     * Hide all modals
     */
    hideAllModals() {
        this.loginModal?.classList.remove('active');
        this.registerModal?.classList.remove('active');
    }

    /**
     * Setup authentication forms
     */
    setupAuthForms() {
        // Login form
        const loginForm = document.getElementById('loginForm');
        if (loginForm) {
            loginForm.addEventListener('submit', async (e) => {
                e.preventDefault();
                await this.handleLogin();
            });
        }

        // Register form
        const registerForm = document.getElementById('registerForm');
        if (registerForm) {
            registerForm.addEventListener('submit', async (e) => {
                e.preventDefault();
                await this.handleRegister();
            });
        }
    }

    /**
     * Handle login submission
     */
    async handleLogin() {
        const email = document.getElementById('loginEmail')?.value;
        const password = document.getElementById('loginPassword')?.value;
        const submitBtn = document.querySelector('#loginForm button[type="submit"]');

        if (!email || !password) {
            toast.error('Please fill in all fields');
            return;
        }

        try {
            submitBtn.disabled = true;
            submitBtn.textContent = 'Logging in...';

            await auth.login(email, password);
            toast.success('Welcome back!');
            
            // Redirect to app
            window.location.href = this.getLoggedInRedirect();
        } catch (error) {
            console.error('Login error:', error);
            toast.error(error.message || 'Invalid email or password');
            submitBtn.disabled = false;
            submitBtn.textContent = 'Log In';
        }
    }

    /**
     * Handle registration submission
     */
    async handleRegister() {
        const venueName = document.getElementById('registerVenue')?.value;
        const email = document.getElementById('registerEmail')?.value;
        const password = document.getElementById('registerPassword')?.value;
        const submitBtn = document.querySelector('#registerForm button[type="submit"]');

        if (!venueName || !email || !password) {
            toast.error('Please fill in all fields');
            return;
        }

        if (password.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }

        try {
            submitBtn.disabled = true;
            submitBtn.textContent = 'Creating account...';
            telemetry.track('register_started', { source: 'landing_modal' });

            const registration = await auth.register({ venueName, email, password });
            telemetry.track('register_completed', { source: 'landing_modal' });
            const verificationEmailSent = registration?.verificationEmailSent !== false;
            if (verificationEmailSent) {
                toast.success(
                    registration?.message ||
                    'Account created successfully!'
                );
                window.location.href = this.getLoggedInRedirect();
                return;
            }

            toast.warning(
                registration?.message ||
                'Account created, but the verification email could not be sent. Open settings to resend it.'
            );
            window.location.href = 'app.html#settings?emailDeliveryFailed=true';
        } catch (error) {
            console.error('Registration error:', error);
            toast.error(error.message || 'Registration failed. Please try again.');
            submitBtn.disabled = false;
            submitBtn.textContent = 'Create Account';
        }
    }

    /**
     * Setup navigation
     */
    setupNavigation() {
        this.navbar = document.querySelector('.navbar');
        this.mobileMenuBtn = document.getElementById('mobileMenuBtn');
        this.mobileNavBackdrop = document.getElementById('mobileNavBackdrop');
        this.siteNavigation = document.getElementById('siteNavigation');

        this.mobileMenuBtn?.addEventListener('click', () => this.toggleMobileMenu());
        this.mobileNavBackdrop?.addEventListener('click', () => this.closeMobileMenu());
        if (window.innerWidth <= 768) {
            this.siteNavigation?.setAttribute('aria-hidden', 'true');
        } else {
            this.siteNavigation?.setAttribute('aria-hidden', 'false');
        }

        // Smooth scrolling for anchor links
        document.querySelectorAll('a[href^="#"]').forEach(anchor => {
            anchor.addEventListener('click', (e) => {
                const targetId = anchor.getAttribute('href');
                if (targetId === '#') return;
                
                const target = document.querySelector(targetId);
                if (target) {
                    e.preventDefault();
                    target.scrollIntoView({ behavior: 'smooth' });
                    this.closeMobileMenu();
                }
            });
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                this.hideAllModals();
                this.closeMobileMenu();
                return;
            }

            if (event.key === 'Tab' && this.mobileMenuOpen) {
                this.trapMobileFocus(event);
            }
        });

        window.addEventListener('resize', () => {
            if (window.innerWidth > 768 && this.mobileMenuOpen) {
                this.closeMobileMenu({ restoreFocus: false });
            }
            if (window.innerWidth > 768) {
                this.siteNavigation?.setAttribute('aria-hidden', 'false');
            } else if (!this.mobileMenuOpen) {
                this.siteNavigation?.setAttribute('aria-hidden', 'true');
            }
        });
    }

    toggleMobileMenu() {
        if (this.mobileMenuOpen) {
            this.closeMobileMenu();
            return;
        }
        this.openMobileMenu();
    }

    openMobileMenu() {
        if (!this.navbar || !this.mobileMenuBtn || !this.mobileNavBackdrop) return;
        this.mobileMenuOpen = true;
        this.navbar.classList.add('mobile-open');
        this.mobileMenuBtn.setAttribute('aria-expanded', 'true');
        this.siteNavigation?.setAttribute('aria-hidden', 'false');
        this.mobileNavBackdrop.hidden = false;
        this.mobileNavBackdrop.classList.add('active');
        document.body.classList.add('mobile-nav-open');

        const firstLink = this.siteNavigation?.querySelector('a, button');
        if (firstLink instanceof HTMLElement) {
            window.setTimeout(() => firstLink.focus(), 0);
        }
    }

    closeMobileMenu(options = {}) {
        if (!this.mobileMenuOpen) return;
        const restoreFocus = options.restoreFocus !== false;
        this.mobileMenuOpen = false;
        this.navbar?.classList.remove('mobile-open');
        this.mobileMenuBtn?.setAttribute('aria-expanded', 'false');
        if (window.innerWidth <= 768) {
            this.siteNavigation?.setAttribute('aria-hidden', 'true');
        }
        if (this.mobileNavBackdrop) {
            this.mobileNavBackdrop.classList.remove('active');
            this.mobileNavBackdrop.hidden = true;
        }
        document.body.classList.remove('mobile-nav-open');

        if (restoreFocus) {
            this.mobileMenuBtn?.focus();
        }
    }

    trapMobileFocus(event) {
        const focusable = this.siteNavigation
            ? Array.from(
                this.siteNavigation.querySelectorAll(
                    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
                )
            ).filter((el) => el.offsetParent !== null)
            : [];

        if (!focusable.length) {
            return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;

        if (!event.shiftKey && active === last) {
            event.preventDefault();
            first.focus();
        } else if (event.shiftKey && active === first) {
            event.preventDefault();
            last.focus();
        }
    }

    setText(id, value) {
        if (!value) return;
        const element = document.getElementById(id);
        if (element) {
            element.textContent = String(value);
        }
    }

    setButtonText(id, value) {
        if (!value) return;
        const button = document.getElementById(id);
        if (button) {
            button.textContent = String(value);
        }
    }

    setMetaContent(selector, value) {
        if (!value) return;
        const element = document.querySelector(selector);
        if (element) {
            element.setAttribute('content', String(value));
        }
    }

    getLoggedInRedirect() {
        return auth.isSuperadmin() ? 'app.html#superadmin/dashboard' : 'app.html#history';
    }

    renderFaq(faqItems) {
        const grid = document.getElementById('cmsFaqGrid');
        if (!grid) return;
        grid.innerHTML = '';

        faqItems.forEach((item) => {
            const question = document.createElement('div');
            question.className = 'faq-item';

            const heading = document.createElement('h4');
            heading.textContent = item?.question ? String(item.question) : 'Question';

            const answer = document.createElement('p');
            answer.textContent = item?.answer ? String(item.answer) : '';

            question.append(heading, answer);
            grid.appendChild(question);
        });
    }
}

// Initialize landing page
document.addEventListener('DOMContentLoaded', () => {
    new LandingPage();
});
